import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { grnService } from '../../services/grnService';
import { apiService } from '../../services/api';
import paymentTypeService, { PaymentType as PaymentTypeOption } from '../../services/paymentTypeService';
import {
  CreateGRNRequest,
  CreateGRNItemRequest,
  GRNType,
  POForGRN,
  POLineForGRN,
} from '../../types';
import './GRNPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save, Plus, X, AlertCircle } from 'lucide-react';

// ─── Local dropdown types ─────────────────────────────────────────────────────

interface SupplierOption  { id: number; name: string; }
interface WarehouseOption { id: number; name: string; }
interface ProductOption   { id: number; name: string; sku: string; unitOfMeasure?: string; unitId?: number; purchasePrice?: number; taxPercentage?: number; }
interface UnitOption      { id: number; unitName: string; }

// UI-only extension (kept for structural consistency with CreateGRNItemRequest)
interface GRNItemUI extends CreateGRNItemRequest {}

// ─── Empty item template ──────────────────────────────────────────────────────

const emptyItem = (): GRNItemUI => ({
  productId: 0,
  purchaseOrderItemId: undefined,
  quantityOrdered: 0,
  quantityReceived: 0,
  unitId: 0,
  unitPrice: 0,
  discountPercentage: 0,
  taxPercentage: 0,
  qualityStatus: 'Good',
  batchNumber: '',
  expiryDate: '',
  manufactureDate: '',
  remarks: '',
  goodQuantity: 0,
  defectiveQuantity: 0,
});

// ─── Component ────────────────────────────────────────────────────────────────

const GRNCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  // GRN type toggle
  const [grnType, setGrnType] = useState<GRNType>('Direct');

  // Header form fields
  const [grnDate, setGrnDate]               = useState(new Date().toISOString().split('T')[0]);
  const [receiptDate, setReceiptDate]       = useState(new Date().toISOString().split('T')[0]);
  const [supplierId, setSupplierId]         = useState<number>(0);
  const [warehouseId, setWarehouseId]       = useState<number>(0);
  const [selectedPoId, setSelectedPoId]     = useState<number>(0);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes]                   = useState('');

  // Items
  const [grnItems, setGrnItems] = useState<GRNItemUI[]>([emptyItem()]);

  // Dropdown data
  const [suppliers, setSuppliers]             = useState<SupplierOption[]>([]);
  const [warehouses, setWarehouses]           = useState<WarehouseOption[]>([]);
  const [products, setProducts]               = useState<ProductOption[]>([]);   // all products (WithPO name lookup + unit map)
  const [supplierProducts, setSupplierProducts] = useState<ProductOption[]>([]); // Direct mode: supplier-filtered
  const [units, setUnits]                     = useState<UnitOption[]>([]);
  const [loadingSupplierProducts, setLoadingSupplierProducts] = useState(false);

  // Payment type
  const [paymentTypes,   setPaymentTypes]   = useState<PaymentTypeOption[]>([]);
  const [paymentTypeId,  setPaymentTypeId]  = useState<number>(0);

  // WithPO state
  const [approvedPOs, setApprovedPOs] = useState<POForGRN[]>([]);
  const [loadingPOs, setLoadingPOs]   = useState(false);

  // UI state
  const [loading, setLoading]               = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [error, setError]                   = useState<string | null>(null);

  // ── Load dropdowns ─────────────────────────────────────────────────────────

  useEffect(() => {
    const loadDropdowns = async () => {
      setLoadingDropdowns(true);

      const mapSuppliers  = (d: any): SupplierOption[]  => (d?.data?.data ?? d?.data ?? []).map((s: any) => ({ id: s.id ?? s.supplierId, name: s.name ?? s.supplierName ?? s.companyName }));
      const mapWarehouses = (d: any): WarehouseOption[] => (d?.data?.data ?? d?.data ?? []).map((w: any) => ({ id: w.id ?? w.warehouseId, name: w.name ?? w.warehouseName }));
      const mapProducts   = (d: any): ProductOption[]   => (d?.data?.data ?? d?.data ?? []).map((p: any) => ({ id: p.id ?? p.productId, name: p.name ?? p.productName, sku: p.sku ?? p.productCode ?? '', unitOfMeasure: p.unitOfMeasure }));
      const mapUnits      = (d: any): UnitOption[]      => (d?.data?.data ?? d?.data ?? []).map((u: any) => ({ id: u.id ?? u.unitId, unitName: u.unitName ?? u.name }));

      const [supRes, whRes, prodRes, unitRes, ptRes] = await Promise.allSettled([
        apiService.get<any>('/suppliers',           { pageSize: 500 }),
        apiService.get<any>('/organization/warehouses', { pageSize: 200 }),
        apiService.get<any>('/products',            { pageSize: 1000, isActive: true }),
        apiService.get<any>('/units',               { pageSize: 200 }),
        paymentTypeService.getActivePaymentTypes('Purchase'),
      ]);

      if (supRes.status === 'fulfilled')  setSuppliers(mapSuppliers(supRes.value.data));
      if (whRes.status === 'fulfilled')   setWarehouses(mapWarehouses(whRes.value.data));
      if (prodRes.status === 'fulfilled') setProducts(mapProducts(prodRes.value.data));
      if (unitRes.status === 'fulfilled') setUnits(mapUnits(unitRes.value.data));
      if (ptRes.status === 'fulfilled') {
        const pts: PaymentTypeOption[] = Array.isArray(ptRes.value) ? ptRes.value : [];
        setPaymentTypes(pts);
        const def = pts.find(p => p.isDefault) ?? pts[0];
        if (def) setPaymentTypeId(def.id);
      }

      const anyFailed = [supRes, whRes, prodRes, unitRes].some(r => r.status === 'rejected');
      if (anyFailed) setError(t('grn.failedToLoadDetails'));

      setLoadingDropdowns(false);
    };
    loadDropdowns();
  }, [t]);

  // ── WithPO: load approved POs ──────────────────────────────────────────────

  const loadApprovedPOs = useCallback(async (sid: number) => {
    if (sid <= 0) { setApprovedPOs([]); return; }
    setLoadingPOs(true);
    try {
      const res = await grnService.getApprovedPOsBySupplier(sid);
      setApprovedPOs(res.data?.data ?? []);
    } catch (err: unknown) {
      setApprovedPOs([]);
      setError(parseApiError(err, t('grn.failedToLoadDetails')).message);
    } finally {
      setLoadingPOs(false);
    }
  }, [t]);

  // ── Direct mode: load products mapped to the selected supplier ────────────

  const loadSupplierProducts = useCallback(async (sid: number) => {
    if (sid <= 0) { setSupplierProducts([]); return; }
    setLoadingSupplierProducts(true);
    try {
      const res = await apiService.get<any>(`/purchase-items/supplier/${sid}`);
      // ApiResponse<List<PurchaseItemDTO>>: { success, data: [...] }
      const items: any[] = res.data?.data ?? [];
      setSupplierProducts(
        items.map((item: any) => ({
          id: item.productId,
          name: item.productName,
          sku: item.productSKU ?? '',
          unitOfMeasure: item.unitOfMeasure,
          unitId: item.unitId,
          purchasePrice: item.purchasePrice,
          taxPercentage: item.taxPercentage ?? 0,
        })).sort((a: ProductOption, b: ProductOption) => a.name.localeCompare(b.name))
      );
    } catch {
      setSupplierProducts([]);
    } finally {
      setLoadingSupplierProducts(false);
    }
  }, []);

  const handleSupplierChange = (sid: number) => {
    setSupplierId(sid);
    setSelectedPoId(0);
    setApprovedPOs([]);
    setSupplierProducts([]);
    if (grnType === 'WithPO' && sid > 0) loadApprovedPOs(sid);
    if (sid > 0) loadSupplierProducts(sid);
  };

  const handleGrnTypeChange = (type: GRNType) => {
    setGrnType(type);
    setSelectedPoId(0);
    setApprovedPOs([]);
    setSupplierProducts([]);
    if (type === 'WithPO' && supplierId > 0) loadApprovedPOs(supplierId);
    if (supplierId > 0) loadSupplierProducts(supplierId);
    setGrnItems([emptyItem()]);
  };

  // ── PO pre-populate ────────────────────────────────────────────────────────

  const handlePOSelect = (poId: number) => {
    setSelectedPoId(poId);
    if (poId <= 0) return;
    const po = approvedPOs.find((p) => p.purchaseOrderId === poId);
    if (!po || po.lines.length === 0) return;
    // Inherit payment type from PO
    if (po.paymentTypeId) setPaymentTypeId(po.paymentTypeId);
    setGrnItems(
      po.lines.map((line: POLineForGRN): CreateGRNItemRequest => {
        const sp = supplierProducts.find((p) => p.id === line.productId);
        // Auto-fill unit: prefer direct unitId; fall back to name-matching
        let unitId = 0;
        if (sp?.unitId) {
          unitId = sp.unitId;
        } else {
          const product = products.find((p) => p.id === line.productId);
          if (product?.unitOfMeasure) {
            const matchedUnit = units.find(
              (u) => u.unitName.toLowerCase() === product.unitOfMeasure!.toLowerCase()
            );
            if (matchedUnit) unitId = matchedUnit.id;
          }
        }
        const taxPct = sp?.taxPercentage ?? 0;
        return {
          productId: line.productId,
          purchaseOrderItemId: line.id,
          quantityOrdered: line.pendingQuantity,
          quantityReceived: line.pendingQuantity,
          unitId,
          unitPrice: line.unitPrice,
          discountPercentage: 0,
          taxPercentage: taxPct,
          qualityStatus: 'Good',
          batchNumber: '',
          expiryDate: '',
          manufactureDate: '',
          remarks: '',
          goodQuantity: line.pendingQuantity,
          defectiveQuantity: 0,
        };
      })
    );
  };

  // ── Item handlers ──────────────────────────────────────────────────────────

  const handleItemChange = (index: number, field: keyof GRNItemUI, value: unknown) => {
    setGrnItems((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;

      // Auto-fill unit, price and tax when a product is selected
      if (field === 'productId' && typeof value === 'number' && value > 0) {
        const sp = supplierProducts.find((p) => p.id === value);
        // Unit: prefer direct unitId from purchase item; fall back to name-matching from all-products
        if (sp?.unitId) {
          next[index].unitId = sp.unitId;
        } else {
          const product = products.find((p) => p.id === value);
          if (product?.unitOfMeasure) {
            const matchedUnit = units.find(
              (u) => u.unitName.toLowerCase() === product.unitOfMeasure!.toLowerCase()
            );
            if (matchedUnit) next[index].unitId = matchedUnit.id;
          }
        }
        // Price + tax: from the supplier purchase-item record
        if (sp?.purchasePrice !== undefined) next[index].unitPrice = sp.purchasePrice;
        next[index].taxPercentage = sp?.taxPercentage ?? 0;
      }

      // In Direct mode qty-ordered tracks qty-received automatically
      if (field === 'quantityReceived' && grnType === 'Direct') {
        next[index].quantityOrdered = next[index].quantityReceived;
      }

      if (field === 'quantityReceived' || field === 'defectiveQuantity') {
        const item = next[index];
        next[index].goodQuantity = Math.max(0, item.quantityReceived - item.defectiveQuantity);
      }
      return next;
    });
  };

  const addItem    = () => setGrnItems((prev) => [...prev, emptyItem()]);
  const removeItem = (i: number) => setGrnItems((prev) => prev.filter((_, idx) => idx !== i));

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (supplierId <= 0)  { setError(t('grn.supplierRequired'));  return; }
    if (warehouseId <= 0) { setError(t('grn.warehouseRequired')); return; }
    if (grnType === 'WithPO' && selectedPoId <= 0) { setError(t('grn.poRequired')); return; }
    const validItems = grnItems.filter((i) => i.productId > 0);
    if (validItems.length === 0) { setError(t('grn.itemsRequired')); return; }

    setLoading(true);
    try {
      const payload: CreateGRNRequest = {
        grnDate,
        receiptDate,
        supplierId,
        warehouseId,
        purchaseOrderId: grnType === 'WithPO' ? selectedPoId : undefined,
        grnType,
        // For WithPO: backend ignores this and inherits from the PO instead.
        // For Direct: user-selected value is used.
        paymentTypeId: paymentTypeId > 0 ? paymentTypeId : undefined,
        notes: notes || undefined,
        referenceNumber: referenceNumber || undefined,
        receivedByUserId: user?.id ?? 0,
        // Strip empty strings from optional date/text fields so .NET doesn't
        // try to deserialize "" as DateTime? (which causes a 400 model error).
        // Also strip isTaxEnabled which is UI-only state.
        grnItems: validItems.map((item) => ({
          ...item,
          expiryDate:      item.expiryDate      || undefined,
          manufactureDate: item.manufactureDate || undefined,
          batchNumber:     item.batchNumber     || undefined,
          remarks:         item.remarks         || undefined,
        })),
      };
      const res = await grnService.createGRN(payload);
      const created = res.data?.data;
      navigate(`/grn/${created?.grnId}`, {
        state: { message: t('grn.createdSuccess', { number: created?.grnNumber }) },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('grn.failedToCreate')).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const selectedPO  = approvedPOs.find((p) => p.purchaseOrderId === selectedPoId);
  const noPOsAvail  = !loadingPOs && supplierId > 0 && approvedPOs.length === 0 && grnType === 'WithPO';

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxAmount = 0;
    grnItems.forEach((item) => {
      if (item.productId <= 0) return;
      const lineAmt  = (item.unitPrice || 0) * (item.quantityReceived || 0);
      const disc     = lineAmt * ((item.discountPercentage || 0) / 100);
      const taxable  = lineAmt - disc;
      const tax      = taxable * ((item.taxPercentage || 0) / 100);
      subtotal      += lineAmt;
      totalDiscount += disc;
      taxAmount     += tax;
    });
    return { subtotal, totalDiscount, taxAmount, grandTotal: subtotal - totalDiscount + taxAmount };
  }, [grnItems]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="grn-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/grn')}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('grn.backToList')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('grn.createTitle')}</h1>
              <p className="ims-page-subtitle">{t('grn.createSubtitle')}</p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="btn-cancel" onClick={() => navigate('/grn')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={loading || loadingDropdowns}>
              {loading ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {t('grn.createGRN')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* ─── LEFT — GRN Type + Header Fields ─── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('grn.grnType')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Type radio cards */}
                <div className="grn-type-cards">
                  {(['Direct', 'WithPO'] as GRNType[]).map((type) => (
                    <label
                      key={type}
                      className={`grn-type-card${grnType === type ? ' grn-type-card--active' : ''}`}
                    >
                      <input
                        type="radio"
                        value={type}
                        checked={grnType === type}
                        onChange={() => handleGrnTypeChange(type)}
                      />
                      <div>
                        <div className="grn-type-card-label">
                          {type === 'Direct' ? t('grn.typeDirect') : t('grn.typeWithPO')}
                        </div>
                        <div className="grn-type-card-desc">
                          {type === 'Direct' ? t('grn.typeDirectDesc') : t('grn.typeWithPODesc')}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Dates */}
                <div className="ims-date-row">
                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('grn.grnDate')} <span className="required">*</span></label>
                    <input type="date" className="ims-form-control" value={grnDate} onChange={(e) => setGrnDate(e.target.value)} required />
                  </div>
                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('grn.receiptDate')} <span className="required">*</span></label>
                    <input type="date" className="ims-form-control" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} required />
                  </div>
                </div>

                {/* Supplier */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.supplier')} <span className="required">*</span></label>
                  <select className="ims-form-control" value={supplierId} disabled={loadingDropdowns} onChange={(e) => handleSupplierChange(Number(e.target.value))}>
                    <option value={0}>{loadingDropdowns ? t('common.loading') : t('grn.selectSupplier')}</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>

                {/* Warehouse */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.warehouse')} <span className="required">*</span></label>
                  <select className="ims-form-control" value={warehouseId} onChange={(e) => setWarehouseId(Number(e.target.value))}>
                    <option value={0}>{t('grn.selectWarehouse')}</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>

                {/* WithPO: PO selector */}
                {grnType === 'WithPO' && (
                  <div className="ims-form-group">
                    <label className="ims-form-label">{t('grn.purchaseOrder')} <span className="required">*</span></label>
                    {supplierId <= 0 ? (
                      <p className="grn-po-hint">{t('grn.selectSupplierFirst')}</p>
                    ) : loadingPOs ? (
                      <span className="grn-loading-badge">
                        <span className="ims-btn-spinner" style={{ borderTopColor: 'var(--ims-accent-light)' }} />
                        {t('grn.loadingPOs')}
                      </span>
                    ) : noPOsAvail ? (
                      <p className="grn-po-hint grn-po-hint--error">{t('grn.noPOsAvailable')}</p>
                    ) : (
                      <select className="ims-form-control" value={selectedPoId} onChange={(e) => handlePOSelect(Number(e.target.value))}>
                        <option value={0}>{t('grn.selectPO')}</option>
                        {approvedPOs.map((po) => (
                          <option key={po.purchaseOrderId} value={po.purchaseOrderId}>
                            {po.poNumber} — {new Date(po.orderDate).toLocaleDateString()} ({po.lines.length} {t('grn.pendingLines')})
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedPO && (
                      <div className="grn-po-badge">
                        <strong>{selectedPO.poNumber}</strong>
                        {' · '}{selectedPO.lines.length} {t('grn.pendingLines')}
                        {' · '}<span style={{ color: 'var(--ims-accent)' }}>{t('grn.itemsLoadedFromPO')}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Type */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('grn.paymentType', 'Payment Type')} <span className="required">*</span>
                  </label>
                  {grnType === 'WithPO' && selectedPoId > 0 ? (
                    <div style={{
                      padding: '8px 12px',
                      background: 'var(--ims-accent-bg)',
                      border: '1px solid var(--ims-accent-ring)',
                      borderRadius: 6,
                      fontSize: 13,
                      color: 'var(--ims-accent)',
                      fontWeight: 600,
                    }}>
                      {paymentTypes.find(p => p.id === paymentTypeId)?.name ?? t('grn.inheritedFromPO', 'Inherited from PO')}
                      <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--ims-text-sub)', marginInlineStart: 8 }}>
                        ({t('grn.inheritedFromPO', 'Inherited from PO')})
                      </span>
                    </div>
                  ) : (
                    <select
                      className="ims-form-control"
                      value={paymentTypeId}
                      onChange={(e) => setPaymentTypeId(Number(e.target.value))}
                      disabled={loadingDropdowns}
                    >
                      <option value={0}>{t('grn.selectPaymentType', 'Select payment type…')}</option>
                      {paymentTypes.map(pt => (
                        <option key={pt.id} value={pt.id}>{pt.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Reference */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.referenceNumber')}</label>
                  <input
                    type="text"
                    className="ims-form-control"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder={t('grn.referenceNumberPlaceholder')}
                  />
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('grn.notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* ─── CENTER — Receipt Items ─── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('grn.receiptItems')}</h2>
                {grnType === 'Direct' && (
                  <button type="button" className="ims-btn ims-btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={addItem}>
                    <Plus size={12} strokeWidth={2.5} />
                    {t('grn.addItem')}
                  </button>
                )}
              </div>
              <div className="ims-lines-body">
                {grnItems.length === 0 ? (
                  <div className="ims-items-empty">
                    <div className="ims-items-empty-icon">📦</div>
                    {grnType === 'WithPO' ? t('grn.selectPOToLoadItems') : t('grn.addItemsManually')}
                  </div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 160 }}>{t('grn.product')}</th>
                          <th className="ims-th" style={{ minWidth: 110 }}>{t('grn.unit')}</th>
                          <th className="ims-th ims-th--num">{t('grn.unitPrice')}</th>
                          {grnType === 'WithPO' && <th className="ims-th ims-th--num">{t('grn.qtyOrdered')}</th>}
                          <th className="ims-th ims-th--num">{t('grn.qtyReceived')}</th>
                          <th className="ims-th" style={{ minWidth: 110 }}>{t('grn.qualityStatus')}</th>
                          <th className="ims-th ims-th--num" style={{ color: 'var(--ims-green-text)' }}>{t('grn.goodQty')}</th>
                          <th className="ims-th ims-th--num" style={{ color: 'var(--ims-red-text)' }}>{t('grn.defectiveQty')}</th>
                          <th className="ims-th" style={{ minWidth: 120 }}>{t('grn.batchNumber')}</th>
                          <th className="ims-th" style={{ minWidth: 130 }}>{t('grn.expiryDate')}</th>
                          <th className="ims-th ims-th--num">% {t('grn.discount')}</th>
                          <th className="ims-th ims-th--num">% {t('grn.tax')}</th>
                          {grnType === 'Direct' && <th className="ims-th ims-th--del" />}
                        </tr>
                      </thead>
                      <tbody>
                        {grnItems.map((item, index) => (
                          <tr key={index} className="ims-tr">
                            {/* Product */}
                            <td className="ims-td">
                              {grnType === 'WithPO' && item.productId > 0 ? (
                                <input className="ims-cell-input" readOnly
                                  value={
                                    selectedPO?.lines.find((l) => l.productId === item.productId)?.productName
                                    ?? products.find((p) => p.id === item.productId)?.name
                                    ?? `Product #${item.productId}`
                                  } />
                              ) : (
                                <select className="ims-cell-input" value={item.productId}
                                  onChange={(e) => handleItemChange(index, 'productId', Number(e.target.value))}
                                  disabled={loadingSupplierProducts || supplierId <= 0}>
                                  <option value={0}>
                                    {supplierId <= 0
                                      ? t('grn.selectSupplierFirst')
                                      : loadingSupplierProducts
                                        ? t('common.loading')
                                        : t('grn.selectProduct')}
                                  </option>
                                  {supplierProducts.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                                </select>
                              )}
                            </td>
                            {/* Unit */}
                            <td className="ims-td">
                              <select className="ims-cell-input" value={item.unitId}
                                onChange={(e) => handleItemChange(index, 'unitId', Number(e.target.value))}>
                                <option value={0}>{t('grn.selectUnit')}</option>
                                {units.map((u) => <option key={u.id} value={u.id}>{u.unitName}</option>)}
                              </select>
                            </td>
                            {/* Unit Price */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Qty Ordered — WithPO mode only */}
                            {grnType === 'WithPO' && (
                              <td className="ims-td ims-td--num">
                                <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                  value={item.quantityOrdered}
                                  readOnly={!!item.purchaseOrderItemId}
                                  onChange={(e) => handleItemChange(index, 'quantityOrdered', parseFloat(e.target.value) || 0)} />
                              </td>
                            )}
                            {/* Qty Received */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                value={item.quantityReceived}
                                onChange={(e) => handleItemChange(index, 'quantityReceived', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Quality Status */}
                            <td className="ims-td">
                              <select className="ims-cell-input" value={item.qualityStatus}
                                onChange={(e) => handleItemChange(index, 'qualityStatus', e.target.value)}>
                                <option value="Good">{t('grn.qualityGood')}</option>
                                <option value="Partial">{t('grn.qualityPartial')}</option>
                                <option value="Defective">{t('grn.qualityDefective')}</option>
                              </select>
                            </td>
                            {/* Good Qty (auto) */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input ims-good-input"
                                value={item.goodQuantity} disabled />
                            </td>
                            {/* Defective Qty */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0}
                                value={item.defectiveQuantity}
                                onChange={(e) => handleItemChange(index, 'defectiveQuantity', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Batch */}
                            <td className="ims-td">
                              <input type="text" className="ims-cell-input"
                                value={item.batchNumber ?? ''}
                                onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                                placeholder={t('grn.batchNumberPlaceholder')} />
                            </td>
                            {/* Expiry */}
                            <td className="ims-td">
                              <input type="date" className="ims-cell-input"
                                value={item.expiryDate ?? ''}
                                onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)} />
                            </td>
                            {/* Discount */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0} max={100}
                                value={item.discountPercentage}
                                onChange={(e) => handleItemChange(index, 'discountPercentage', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Tax */}
                            <td className="ims-td ims-td--num">
                              <input type="number" className="ims-cell-input ims-num-input" step="0.01" min={0} max={100}
                                value={item.taxPercentage}
                                onChange={(e) => handleItemChange(index, 'taxPercentage', parseFloat(e.target.value) || 0)} />
                            </td>
                            {/* Delete */}
                            {grnType === 'Direct' && (
                              <td className="ims-td ims-td--del">
                                <button type="button" className="ims-remove-btn"
                                  onClick={() => removeItem(index)}
                                  disabled={grnItems.length === 1}
                                  title={t('common.remove')}>
                                  <X size={12} strokeWidth={2.5} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Totals footer ── */}
              {grnItems.some((i) => i.productId > 0) && (
                <div className="grn-totals">
                  <div className="grn-totals-row">
                    <span className="grn-totals-label">{t('grn.subtotal')}</span>
                    <span className="grn-totals-value">{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="grn-totals-row">
                    <span className="grn-totals-label">{t('grn.totalDiscount')}</span>
                    <span className="grn-totals-value grn-totals-value--discount">− {totals.totalDiscount.toFixed(2)}</span>
                  </div>
                  <div className="grn-totals-row">
                    <span className="grn-totals-label">{t('grn.taxAmount')}</span>
                    <span className="grn-totals-value">{totals.taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="grn-totals-row grn-totals-row--grand">
                    <span className="grn-totals-label">{t('grn.grandTotal')}</span>
                    <span className="grn-totals-value grn-totals-value--grand">{totals.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default GRNCreatePage;
